import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Chip, ListItemButton, Stack, Typography } from "@mui/material";
import CustomScrollbars from "components/CustomScrollbars";
import FileSearch from "components/fileStorage/FileSearch";
import {
  connectionLabel,
  useGetConnectionQuery,
  useGetConnectionsQuery,
} from "features/integrationsBeta/connections";
import type { ReadinessIssue } from "features/integrationsBeta/integrationsBeta";
import { worstSeverity } from "features/integrationsBeta/readinessUi";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { useEffect, useState } from "react";
import ConnectionCard from "./ConnectionCard";
import ConnectionCreateDialog from "./ConnectionCreateDialog";

export interface ConnectionGroup {
  label: string;
  ids: number[];
}

function ConnItem({
  id,
  selected,
  search,
  issues,
  onSelect,
}: {
  id: number;
  selected: boolean;
  search: string;
  issues?: ReadinessIssue[];
  onSelect: (id: number) => void;
}) {
  const { data } = useGetConnectionQuery(id);
  const name = data ? connectionLabel(data) : `Connection ${id}`;
  const searchable = data ? `${name} ${data.provider_name}` : name;
  if (search && !searchable.toLowerCase().includes(search.toLowerCase()))
    return null;
  const severity = worstSeverity(issues);
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(id)}
      sx={{ borderRadius: 1, gap: 1 }}
    >
      <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
        {name}
      </Typography>
      {severity && (
        <WarningAmberIcon
          fontSize="small"
          color={severity === "error" ? "error" : "warning"}
        />
      )}
      {data?.type ? (
        <Chip label={data.type} size="small" variant="outlined" />
      ) : null}
    </ListItemButton>
  );
}

interface Props {
  groups: ConnectionGroup[];
  focus?: { id: number; nonce: number } | null;
  issuesByConnection?: Record<number, ReadinessIssue[]>;
  /** When set, ALL of the client's connections are listed (pipeline groups
   * first, the rest under "Other client connections") and superusers can
   * create new connections from here. */
  clientId?: number;
}

/**
 * Master–detail Connections view: a narrow, searchable list grouped by the
 * pipeline stages that use each connection (plus the webhook Secret-Key
 * connection and, when ``clientId`` is given, every other connection of the
 * client) on the left, and the selected connection's card (config +
 * credential slots) on the right.
 */
export default function ConnectionMasterDetail({
  groups,
  focus,
  issuesByConnection = {},
  clientId,
}: Props) {
  const { isSuperuser } = usePlatformRole();
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const { data: clientConnections = [] } = useGetConnectionsQuery(
    { client: clientId as number },
    { skip: clientId == null },
  );

  const pipelineIds = new Set(groups.flatMap((group) => group.ids));
  const otherIds = clientConnections
    .map((connection) => connection.id)
    .filter((id) => !pipelineIds.has(id));

  const allGroups: ConnectionGroup[] = [
    ...groups,
    ...(otherIds.length
      ? [{ label: "Other client connections", ids: otherIds }]
      : []),
  ];

  const allIds = allGroups.flatMap((group) => group.ids);
  const allIdsKey = allIds.join(",");

  // allIdsKey stands in for allIds (a fresh array each render) as the dep.
  useEffect(() => {
    if (selected == null || !allIds.includes(selected)) {
      setSelected(allIds[0] ?? null);
    }
  }, [allIdsKey, selected]);

  // Jump requested from outside (readiness banner, step chip, webhook panel).
  useEffect(() => {
    if (focus && allIds.includes(focus.id)) {
      setSelected(focus.id);
    }
  }, [focus, allIdsKey]);

  const newConnectionButton =
    isSuperuser && clientId != null ? (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setCreateOpen(true)}
      >
        New connection
      </Button>
    ) : null;

  const createDialog =
    clientId != null ? (
      <ConnectionCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={clientId}
        onCreated={(id) => setSelected(id)}
      />
    ) : null;

  if (!allIds.length) {
    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          No connections yet. Connections appear here once pipeline steps (or
          a webhook trigger) reference them, or when they are created for the
          client.
        </Typography>
        {newConnectionButton}
        {createDialog}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <FileSearch
            onSearch={(t) => setSearch(t ?? "")}
            label="Search"
            size="small"
            fullWidth
          />
        </Box>
        {newConnectionButton && (
          <Box sx={{ mb: 1 }}>{newConnectionButton}</Box>
        )}
        <CustomScrollbars
          variant="compact"
          sx={{ maxHeight: 460, minHeight: 0 }}
        >
          <Stack spacing={1.5} sx={{ pr: 0.5 }}>
            {allGroups.map((group) => (
              <Box key={group.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 1, fontWeight: 600 }}
                >
                  {group.label} ({group.ids.length})
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {group.ids.map((id) => (
                    <ConnItem
                      key={id}
                      id={id}
                      selected={selected === id}
                      search={search}
                      issues={issuesByConnection[id]}
                      onSelect={setSelected}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </CustomScrollbars>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selected != null ? (
          <ConnectionCard
            // Remount per connection so no component state (revealed
            // secrets, dialogs, drafts) can leak between connections.
            key={selected}
            connectionId={selected}
            issues={issuesByConnection[selected]}
            // Only connections outside the pipeline are deletable - bound
            // ones are PROTECTed by the backend anyway.
            deletable={isSuperuser && !pipelineIds.has(selected)}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a connection.
          </Typography>
        )}
      </Box>
      {createDialog}
    </Box>
  );
}
